import { Dispatch, FC, ReactElement, SetStateAction, useEffect, useMemo } from 'react';
import axios from 'axios';
import { NextComponentType } from 'next';
import { useAuthSession } from '../hooks/use-auth-session';
import { handleLogout } from '../utils/helpers/auth.helpers';

export const AuthGuard: FC<{
  pageComponent: NextComponentType & { auth?: boolean };
  session: never;
  setInterval: Dispatch<SetStateAction<number>>;
}> = ({ session, pageComponent, setInterval, children }) => {
  /**
   * effect to redirect user to login page without error on first time
   * when user loads application in a new tab
   */
  if (pageComponent.auth === false) {
    // These pages do not require authentication
    return children as ReactElement;
  }

  return (
    <AuthenticatedContent session={session} setInterval={setInterval}>
      {children}
    </AuthenticatedContent>
  );
};

const AuthenticatedContent: FC<{ session: never; setInterval: Dispatch<SetStateAction<number>> }> = ({
  session,
  setInterval,
  children,
}) => {
  const { user, status, error, accessToken, accessTokenExpiry } = useAuthSession();

  useMemo(() => {
    if (!accessToken) {
      delete axios.defaults.headers.common.Authorization;
    } else {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    }
  }, [accessToken]);

  useEffect(() => {
    if (error) {
      handleLogout();
    }

    if (session) {
      const timeRemaining = Math.round((accessTokenExpiry - 6000 - Date.now()) / 1000);

      //      console.log('I am called', timeRemaining, session);

      setInterval(timeRemaining > 0 ? timeRemaining : 0);
    }
  }, [error, accessTokenExpiry, session, setInterval]);

  if (status === 'loading' || !user) {
    return <></>;
  }

  return children as ReactElement;
};
